import {
  Box,
  type BoxProps,
  FormControl,
  FormLabel,
  type FormLabelProps,
  HStack,
  Text,
  type TextProps,
  VStack,
} from "@chakra-ui/react";
import React from "react";
import { entityProfileModalBorders, entityProfileModalColors } from "../entityProfileModal.theme";

const fieldLabelProps: TextProps = {
  color: entityProfileModalColors.bodyText,
  fontSize: "14px",
  fontWeight: 700,
};
const formFieldLabelProps: FormLabelProps = {
  color: entityProfileModalColors.bodyText,
  fontSize: "14px",
  fontWeight: 700,
};

function handleClickableKeyDown(
  event: React.KeyboardEvent<HTMLElement>,
  onClick: (() => void) | undefined
) {
  if (onClick === undefined || (event.key !== "Enter" && event.key !== " ")) {
    return;
  }

  event.preventDefault();
  onClick();
}

export function EntityProfileFieldSection(props: {
  children: React.ReactNode;
  maxW?: BoxProps["maxW"];
  title?: string;
  titleProps?: TextProps;
}) {
  return (
    <Box maxW={props.maxW}>
      {props.title === undefined ? null : (
        <Text
          color={entityProfileModalColors.text}
          fontSize="24px"
          fontWeight={300}
          mb={5}
          {...props.titleProps}
        >
          {props.title}
        </Text>
      )}
      <VStack alignItems="stretch" spacing={4}>
        {props.children}
      </VStack>
    </Box>
  );
}

export function EntityProfileCompactFieldGroup(props: { children: React.ReactNode }) {
  return (
    <VStack alignItems="stretch" spacing={0}>
      {props.children}
    </VStack>
  );
}

export function EntityProfileCompactFieldRow(props: {
  isClickable?: boolean;
  label: string;
  value: React.ReactNode;
  onClick?: () => void;
}) {
  const isClickable = props.isClickable === true && props.onClick !== undefined;

  return (
    <HStack alignItems="baseline" minH="28px" spacing={1}>
      <Text
        {...fieldLabelProps}
        _hover={isClickable ? { color: entityProfileModalColors.active } : undefined}
        cursor={isClickable ? "pointer" : undefined}
        role={isClickable ? "button" : undefined}
        tabIndex={isClickable ? 0 : undefined}
        onClick={isClickable ? props.onClick : undefined}
        onKeyDown={(event) => handleClickableKeyDown(event, props.onClick)}
      >
        {props.label}:
      </Text>
      <Box
        _hover={isClickable ? { color: entityProfileModalColors.active } : undefined}
        color={entityProfileModalColors.text}
        cursor={isClickable ? "pointer" : undefined}
        fontSize="14px"
        minW={0}
        role={isClickable ? "button" : undefined}
        tabIndex={isClickable ? 0 : undefined}
        onClick={isClickable ? props.onClick : undefined}
        onKeyDown={(event) => handleClickableKeyDown(event, props.onClick)}
      >
        {props.value}
      </Box>
    </HStack>
  );
}

export function EntityProfileSplitFieldRow(props: { label: string; value: React.ReactNode }) {
  return (
    <HStack
      alignItems="flex-start"
      borderBottom={entityProfileModalBorders.default}
      justifyContent="space-between"
      minH="42px"
      py={2}
      spacing={6}
    >
      <Text {...fieldLabelProps} color={entityProfileModalColors.labelText}>
        {props.label}
      </Text>
      <Text color={entityProfileModalColors.muted} fontSize="14px" textAlign="right">
        {props.value}
      </Text>
    </HStack>
  );
}

export function EntityProfileInlineControlRow(props: { children: React.ReactNode; label: string }) {
  return (
    <HStack alignItems="center" minH="42px" pb={5} spacing={4}>
      <Text {...fieldLabelProps}>{props.label}</Text>
      {props.children}
    </HStack>
  );
}

export function EntityProfileControlFieldRow(props: {
  children: React.ReactNode;
  label: string;
  labelWidth?: string;
}) {
  return (
    <FormControl
      alignItems="center"
      display="grid"
      gap={3}
      gridTemplateColumns={`${props.labelWidth ?? "170px"} minmax(0, 1fr)`}
    >
      <FormLabel {...formFieldLabelProps} mb={0}>
        {props.label}:
      </FormLabel>
      <Text as="div" fontSize="14px" minH="24px">
        {props.children}
      </Text>
    </FormControl>
  );
}
